import { useEffect, useState } from "react";
import { api, formatApiError, POD_STATUSES, labelOf } from "@/lib/api";
import { PodBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { PackageCheck, ShieldCheck, Upload, Search } from "lucide-react";

const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function PODCenter() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null); // dispatch object or null
  const [form, setForm] = useState({ pod_date: "", pod_received_from: "", pod_received_by: "", pod_remarks: "", pod_status: "received" });
  const [file, setFile] = useState(null);

  const load = async () => {
    const params = {};
    if (status && status !== "all") params.status = ""; // status here filters by pod_status in list; fallback to full list
    const { data } = await api.get("/dispatches");
    const filtered = data.filter((d) => status === "all" ? true : d.pod_status === status);
    setItems(filtered);
  };
  useEffect(() => { load(); }, [status]);

  const openForm = (d) => {
    setOpen(d);
    setForm({
      pod_date: d.pod_date || new Date().toISOString().slice(0, 10),
      pod_received_from: d.pod_received_from || "",
      pod_received_by: d.pod_received_by || "",
      pod_remarks: d.pod_remarks || "",
      pod_status: "received",
    });
    setFile(null);
  };

  const submit = async () => {
    if (!open) return;
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
    if (file) fd.append("file", file);
    try {
      await api.post(`/dispatches/${open.id}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("POD updated");
      setOpen(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const verify = async (id) => {
    try {
      await api.post(`/dispatches/${id}/pod/verify`);
      toast.success("POD verified"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="page-pod" className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Fulfilment</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1">POD Center</h1>
          <p className="text-sm text-slate-500 mt-1">Upload delivery proof, capture the receiver, and verify against the client.</p>
        </div>
        <select data-testid="pod-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          <option value="all">All</option>
          {POD_STATUSES.map((s) => <option key={s} value={s}>{labelOf(s)}</option>)}
        </select>
      </div>

      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2 max-w-xl">
        <Search size={14} className="text-slate-400" />
        <input
          data-testid="input-pod-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search dispatch code, invoice, LR, received by…"
          className="flex-1 outline-none text-sm bg-transparent"
        />
        {q && <button onClick={() => setQ("")} className="text-xs text-slate-500 hover:text-slate-700">Clear</button>}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 text-left">
              <th className="px-5 py-3">Dispatch</th><th className="px-5 py-3">Invoice</th>
              <th className="px-5 py-3">LR</th><th className="px-5 py-3">Amount</th>
              <th className="px-5 py-3">POD Date</th><th className="px-5 py-3">Received By</th>
              <th className="px-5 py-3">Status</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const qq = q.trim().toLowerCase();
              const visible = qq
                ? items.filter((d) => [d.dispatch_code, d.invoice_number, d.lr_number, d.pod_received_by, d.pod_received_from, d.pod_status]
                    .filter(Boolean).some((v) => String(v).toLowerCase().includes(qq)))
                : items;
              return (<>
              {visible.map((d) => (
              <tr key={d.id} data-testid={`pod-row-${d.id}`} className="table-row border-t border-slate-100">
                <td className="px-5 py-3 font-mono text-xs">{d.dispatch_code}</td>
                <td className="px-5 py-3">{d.invoice_number}</td>
                <td className="px-5 py-3 font-mono text-xs">{d.lr_number}</td>
                <td className="px-5 py-3">{fmtInr(d.invoice_amount)}</td>
                <td className="px-5 py-3 text-xs text-slate-500">{d.pod_date || "-"}</td>
                <td className="px-5 py-3">{d.pod_received_by || "-"}</td>
                <td className="px-5 py-3"><PodBadge status={d.pod_status} /></td>
                <td className="px-5 py-3 text-right space-x-3">
                  <button data-testid={`btn-upload-pod-${d.id}`} onClick={() => openForm(d)} className="text-xs text-amber-700 hover:text-amber-900 font-semibold inline-flex items-center gap-1">
                    <Upload size={12} /> Upload
                  </button>
                  {d.pod_status === "received" && (
                    <button data-testid={`btn-verify-pod-${d.id}`} onClick={() => verify(d.id)} className="text-xs text-emerald-700 hover:text-emerald-900 font-semibold inline-flex items-center gap-1">
                      <ShieldCheck size={12} /> Verify
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan="8" className="py-8 text-center text-slate-500">{q ? `No matches for "${q}"` : "No matching dispatches"}</td></tr>}
            </>);
            })()}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-40" onClick={() => setOpen(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 w-full max-w-xl" data-testid="dialog-pod">
            <h2 className="font-heading text-xl font-bold mb-1 flex items-center gap-2"><PackageCheck size={18} /> POD for {open.dispatch_code}</h2>
            <div className="text-xs text-slate-500 mb-4">Invoice {open.invoice_number} · LR {open.lr_number}</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">POD Date</label>
                <input data-testid="pod-date" type="date" value={form.pod_date} onChange={(e) => setForm({ ...form, pod_date: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">POD Status</label>
                <select value={form.pod_status} onChange={(e) => setForm({ ...form, pod_status: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm">
                  {POD_STATUSES.map((s) => <option key={s} value={s}>{labelOf(s)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Received From (Transporter/Person)</label>
                <input data-testid="pod-received-from" value={form.pod_received_from} onChange={(e) => setForm({ ...form, pod_received_from: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Received By (Client Recipient Name)</label>
                <input data-testid="pod-received-by" value={form.pod_received_by} onChange={(e) => setForm({ ...form, pod_received_by: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Remarks</label>
                <textarea rows={2} value={form.pod_remarks} onChange={(e) => setForm({ ...form, pod_remarks: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">POD File (PDF or Image)</label>
                <input data-testid="pod-file" type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full mt-1 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setOpen(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button data-testid="btn-save-pod" onClick={submit} className="px-4 py-2 text-sm bg-[var(--navy)] text-white rounded-md">Save POD</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
